import { uniqueId } from 'lodash';
import { Minus, Plus, Settings } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthorization } from '@/auth/hooks/useAuthorization';
import { AssignedUserSelect } from '@/components/AssignedUserSelect';
import { isAssignableAssignedUser } from '@/components/assigned-user.utils';
import { TRAMITE_LABELS } from '@/common/constants';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import EditableTitle from '@/components/ui/EditableTitle';
import TitleSkeleton from '@/components/ui/TitleSkeleton';
import { useAlert } from '../../hooks/useAlert';
import { useDefaultSubtramites } from '../../hooks/useDefaultSubtramites';
import { useGoBackSimple } from '../../hooks/useGoBack';
import { tramiteParametroService } from '../../services/tramiteParametroService';
import { updateTramite } from '../../services/tramiteService';
import CompradorVendedorFieldWithAutocomplete from '../Fields/CompradorVendedorFieldWithAutocomplete';
import Field from '../Fields/Field';
import AppLayout from '../Layouts/AppLayout';
import { AVAILABLE_SUBTRAMITES } from '../Pages/SystemConfiguration/types';
import { SelectGroupOfPeopleButton } from '../SelectGroupOfPeopleButton';

const COMPRADOR_DEFAULT_STATE = {
  key: uniqueId('comprador'),
  nombre: '',
  email: '',
  dni: '',
  telefono: '',
};

const VENDEDOR_DEFAULT_STATE = {
  key: uniqueId('vendedor'),
  nombre: '',
  email: '',
  dni: '',
  telefono: '',
};

const padDatePart = (value) => String(value).padStart(2, '0');

const formatDateTimeLocal = (value) => {
  if (!value) return '';

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return '';
    return `${value.getFullYear()}-${padDatePart(value.getMonth() + 1)}-${padDatePart(value.getDate())}T${padDatePart(value.getHours())}:${padDatePart(value.getMinutes())}`;
  }

  if (typeof value === 'string') {
    const hasTimezone = /Z$|[+-]\d{2}:\d{2}$/.test(value);
    const localMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2}))?/);

    if (localMatch && !hasTimezone) {
      const [, year, month, day, hours = '00', minutes = '00'] = localMatch;
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '';

    return `${parsed.getFullYear()}-${padDatePart(parsed.getMonth() + 1)}-${padDatePart(parsed.getDate())}T${padDatePart(parsed.getHours())}:${padDatePart(parsed.getMinutes())}`;
  }

  return '';
};

const TramiteComponent = () => {
  //Controlo que venga el parametro de tramite por ruta
  const { state } = useLocation();
  const goBack = useGoBackSimple();
  const isUpdate = !!state?.tramite;
  const { errorAlert, successAlert } = useAlert();
  const [data, setData] = useState(state?.tramite || {
    propiedad: '',
    numregistro: '',
    fecini: new Date(),
    fecfirma: new Date(),
    assigned_user_ids: [],
    assigned_users: [],
    compradores: [COMPRADOR_DEFAULT_STATE],
    vendedores: [VENDEDOR_DEFAULT_STATE],
    vendedoresTitle: TRAMITE_LABELS.TRANSFERENTES,
    compradoresTitle: TRAMITE_LABELS.ADQUIRIENTES
  });
  const [loading, setLoading] = useState(false);
  const [loadingParameters, setLoadingParameters] = useState(false);
  const { canEditTitle } = useAuthorization();

  // Hook para manejo de subtramites por defecto
  const {
    createTramiteWithDefaultSubtramites,
    isCreating,
    createError,
    hasDefaultSubtramites,
    defaultSubtramites,
    defaultSubtramitesCount,
    lastCreationResult,
    clearLastResult
  } = useDefaultSubtramites();

  // Función para cargar parámetros existentes
  const loadTitleParameters = async (tramiteId) => {
    try {
      setLoadingParameters(true);

      // Load parameters in parallel for better performance
      const [vendedoresTitleParam, compradoresTitleParam] = await Promise.all([
        tramiteParametroService.getValueOrDefault(tramiteId, 'vendedoresTitle', TRAMITE_LABELS.TRANSFERENTES),
        tramiteParametroService.getValueOrDefault(tramiteId, 'compradoresTitle', TRAMITE_LABELS.ADQUIRIENTES)
      ]);

      setData(prev => ({
        ...prev,
        vendedoresTitle: vendedoresTitleParam || TRAMITE_LABELS.TRANSFERENTES,
        compradoresTitle: compradoresTitleParam || TRAMITE_LABELS.ADQUIRIENTES
      }));
    } catch (error) {
      // Silently use defaults if there's any error (404s are expected for new tramites)
      setData(prev => ({
        ...prev,
        vendedoresTitle: TRAMITE_LABELS.TRANSFERENTES,
        compradoresTitle: TRAMITE_LABELS.ADQUIRIENTES
      }));
    } finally {
      setLoadingParameters(false);
    }
  }

  // Cargar parámetros cuando se está editando un trámite
  useEffect(() => {
    if (isUpdate && data.id) {
      loadTitleParameters(data.id);
    }
  }, [isUpdate, data.id]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isUpdate) {
      // Limpiar resultado anterior
      clearLastResult();

      // Para crear trámite, usar la nueva funcionalidad con subtramites por defecto
      createTramiteWithDefaultSubtramites({ data });
    } else {
      //is update
      setLoading(true);
      updateTramite({ id: data.id, data })
        .then(r => {
          successAlert({ message: 'Trámite editado con éxito' });
          goBack();
        })
        .catch(err => errorAlert({ exception: err }))
        .finally(() => setLoading(false));
    }
  }

  // Efecto para manejar el resultado de la creación con subtramites
  useEffect(() => {
    if (lastCreationResult) {
      const { tramite, initializedSubtramites, errors } = lastCreationResult;

      // Crear los parámetros de títulos custom si existen
      if (data.vendedoresTitle !== 'Vendedores') {
        saveStringParameter({ tramiteId: tramite.id, key: 'vendedoresTitle', value: data.vendedoresTitle });
      }
      if (data.compradoresTitle !== 'Compradores') {
        saveStringParameter({ tramiteId: tramite.id, key: 'compradoresTitle', value: data.compradoresTitle });
      }

      // Mostrar mensaje de éxito con información de subtramites
      let message = 'Trámite guardado con éxito';
      if (initializedSubtramites.length > 0) {
        message += ` • ${initializedSubtramites.length} subtramites inicializados automáticamente`;
      }

      successAlert({ message });

      // Mostrar errores si los hay
      if (errors.length > 0) {
        errorAlert({
          title: 'Advertencias durante la creación',
          message: errors.join(', ')
        });
      }

      goBack();
    }
  }, [lastCreationResult, data.vendedoresTitle, data.compradoresTitle, successAlert, errorAlert, goBack]);

  // Efecto para manejar errores de creación
  useEffect(() => {
    if (createError) {
      errorAlert({
        title: 'Error al crear trámite',
        exception: createError
      });
    }
  }, [createError, errorAlert]);

  const handleChange = ({ name, value }) => {
    setData({
      ...data,
      [name]: value
    });
  }

  const handleChangeCompradorVendedor = (type, index) => {
    return (values) => {
      const newArray = [...data[type]];
      newArray[index] = values;
      setData({
        ...data,
        [type]: newArray
      });
    }
  }

  const addCompradorVendedor = (type) => {
    setData({
      ...data,
      [type]: [
        ...data[type],
        { ...COMPRADOR_DEFAULT_STATE, key: uniqueId(type) }
      ]
    })
  };

  const removeCompradorVendedor = (type, index) => {
    const value = [...data[type]];
    value.splice(index, 1);
    setData({
      ...data,
      [type]: value,
    });
  };


  const peopleIsEmpty = (person) => {
    return !person.nombre && !person.email && !person.dni && !person.telefono;
  }

  const mergePeople = (type, data, people) => {
    const currentPeople = data[type].filter(p => !peopleIsEmpty(p));
    const newPeople = people.map(p => {
      return {
        key: uniqueId(type),
        nombre: p.name,
        email: p.email,
        dni: p.dni,
        telefono: p.phone
      }
    });

    return currentPeople.concat(newPeople);
  }

  const handleSelectGroupChange = (type) => {
    return (value) => {
      setData({
        ...data,
        [type]: mergePeople(type, data, value.people)
      });
    }
  }

  const saveStringParameter = async ({ tramiteId, key, value }) => {
    try {
      await tramiteParametroService.createOrUpdate(tramiteId, key, value.trim(), 'string');
    } catch (error) {
      console.error('Error saving title parameter:', error);
      errorAlert({
        title: 'Error al guardar título',
        exception: error
      });
    }
  };

  // Función para guardar títulos como parámetros
  const handleTitleChange = async (paramName, newTitle) => {
    // Always update the local state first
    handleChange({ name: paramName, value: newTitle });

    // Only save to parameters if we have a tramite ID and the title is not empty
    if (data.id && newTitle.trim() !== '') {
      await saveStringParameter({ tramiteId: data.id, key: paramName, value: newTitle });
    }
  }

  // Helper para obtener nombres de subtramites
  const getSubtramiteNames = (subtramiteKeys) => {
    return subtramiteKeys.map(key => {
      const subtramite = AVAILABLE_SUBTRAMITES.find(sub => sub.key === key);
      return subtramite ? subtramite.name : key;
    });
  }

  // Determinar si está cargando (crear o actualizar)
  const isLoading = loading || isCreating;


  return (
    <AppLayout
      title="Tramite"
    >
      <div className="bg-white p-4 max-w-7xl mx-auto shadow-md rounded-md overflow-hidden">
        <form method="POST" onSubmit={handleSubmit} className="space-y-6">
          <div className="w-full">
            <Field
              type="text"
              name="propiedad"
              label="Propiedad"
              value={data.propiedad}
              onChange={handleChange}
            />
          </div>

          <div className="w-full">
            <Field
              type="select"
              name="numregistro"
              label="# Registro"
              value={data.numregistro}
              onChange={handleChange}
              options={['155', '207', '486']}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <Field
                type="date"
                name="fecini"
                label="Fecha de inicio"
                value={data.fecini}
                onChange={handleChange}
              />
            </div>

            <div className="space-y-4">
              <Field
                type="datetime-local"
                name="fecfirma"
                label="Fecha y hora estimada de firma"
                value={formatDateTimeLocal(data.fecfirma)}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Usuario asignado</label>
            <AssignedUserSelect
              value={data.assigned_users?.[0]
                ? {
                    id: String(data.assigned_users[0].id),
                    nombre: data.assigned_users[0].name,
                    email: data.assigned_users[0].email,
                    userId: String(data.assigned_users[0].id),
                    source: 'user',
                  }
                : null}
              onChange={(user) => {
                setData({
                  ...data,
                  assigned_user_ids: user ? [Number(user.id)] : [],
                  assigned_users: user ? [{
                    id: Number(user.id),
                    name: user.nombre,
                    email: user.email,
                  }] : [],
                });
              }}
              placeholder="Buscar usuario responsable"
              ariaLabel="Usuario asignado"
              disabled={isLoading}
              filterOption={isAssignableAssignedUser}
            />
            <p className="text-xs text-gray-500">
              Este usuario será el encargado de resolver el trámite.
            </p>
          </div>

          {/* Información de subtramites por defecto - solo mostrar al crear */}
          {!isUpdate && hasDefaultSubtramites() && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <Settings className="h-5 w-5 text-blue-600 mt-0.5" />
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-blue-900 mb-2">
                    Subtramites que se inicializarán automáticamente
                  </h3>
                  <p className="text-xs text-blue-700 mb-3">
                    Según la configuración del sistema, se crearán automáticamente {defaultSubtramitesCount} subtramites:
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {getSubtramiteNames(defaultSubtramites).map((name, index) => (
                      <Badge key={index} variant="secondary" className="bg-blue-100 text-blue-800 text-xs">
                        {name}
                      </Badge>
                    ))}
                  </div>
                  <p className="text-xs text-blue-600 mt-2">
                    Podrás agregar subtramites adicionales manualmente después de crear el trámite.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <div className="space-y-4 min-w-0">
              <div className="border-b-2 border-red-500 text-center pb-2 mb-4">
                {loadingParameters ? (
                  <TitleSkeleton className="text-lg font-semibold text-gray-700" />
                ) : (
                  <EditableTitle
                    readOnly={!canEditTitle}
                    title={data.vendedoresTitle || TRAMITE_LABELS.TRANSFERENTES}
                    onSave={(newTitle) => handleTitleChange('vendedoresTitle', newTitle)}
                    className="text-lg font-semibold text-gray-700 justify-center"
                    inputClassName="text-lg font-semibold text-gray-700 text-center"
                  />
                )}
              </div>
              <SelectGroupOfPeopleButton onSelectChange={handleSelectGroupChange('vendedores')} />
              <div className="space-y-4">
                {data.vendedores.map((c, index) => (
                  <div key={c.key || c.id} className="relative">
                    <CompradorVendedorFieldWithAutocomplete onChange={handleChangeCompradorVendedor('vendedores', index)} value={c} />
                    <div className="flex gap-2 mt-2 justify-end">
                      {index === data.vendedores.length - 1 && (
                        <Button
                          variant="outline"
                          size="sm"
                          type="button"
                          onClick={() => addCompradorVendedor('vendedores')}
                          className="rounded-full w-8 h-8 p-0 text-blue-500 hover:bg-blue-50"
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      )}
                      {index !== 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          type="button"
                          onClick={() => removeCompradorVendedor('vendedores', index)}
                          className="rounded-full w-8 h-8 p-0 text-red-500 hover:bg-red-50"
                        >
                          <Minus className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-4 min-w-0">
              <div className="border-b-2 border-red-500 text-center pb-2 mb-4">
                {loadingParameters ? (
                  <TitleSkeleton className="text-lg font-semibold text-gray-700" />
                ) : (
                  <EditableTitle
                    readOnly={!canEditTitle}
                    title={data.compradoresTitle || TRAMITE_LABELS.ADQUIRIENTES}
                    onSave={(newTitle) => handleTitleChange('compradoresTitle', newTitle)}
                    className="text-lg font-semibold text-gray-700 justify-center"
                    inputClassName="text-lg font-semibold text-gray-700 text-center"
                  />
                )}
              </div>
              <SelectGroupOfPeopleButton onSelectChange={handleSelectGroupChange('compradores')} />
              <div className="space-y-4">
                {data.compradores.map((c, index) => (
                  <div key={c.key || c.id} className="relative">
                    <CompradorVendedorFieldWithAutocomplete onChange={handleChangeCompradorVendedor('compradores', index)} value={c} />
                    <div className="flex gap-2 mt-2 justify-end">
                      {index === data.compradores.length - 1 && (
                        <Button
                          variant="outline"
                          size="sm"
                          type="button"
                          onClick={() => addCompradorVendedor('compradores')}
                          className="rounded-full w-8 h-8 p-0 text-blue-500 hover:bg-blue-50"
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      )}
                      {index !== 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          type="button"
                          onClick={() => removeCompradorVendedor('compradores', index)}
                          className="rounded-full w-8 h-8 p-0 text-red-500 hover:bg-red-50"
                        >
                          <Minus className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex gap-2 pt-5 border-t border-gray-200">
            <Button
              variant="outline"
              disabled={isLoading}
              type="button"
              onClick={goBack}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="outline"
              loading={isLoading}
            >
              {isUpdate ? 'Actualizar' : 'Crear Trámite'}
            </Button>
          </div>
        </form>
      </div>
    </AppLayout>
  );
}

export default TramiteComponent;
